// Phase 2.7 — Monthly NK/RR payable summary table extracted from
// MonthlyAccounting.jsx. Pure presentational; parent owns settlement-panel
// open state + alloc detail open state.

import { Section } from "@/components/Shared";
import { CheckCircle2, Circle } from "lucide-react";
import { fmtINR, fmtPeriod } from "@/lib/api";

export default function MonthlySummaryTab({
  filtered,
  monthFilter, setMonthFilter,
  onOpenSettlement, onOpenAlloc,
}) {
  return (
    <Section title="Monthly Summary — NK/RR Payable" testid="ma-summary">
      <div className="p-3 text-[11.5px] text-[var(--muted)] bg-[var(--surface-2)] border-b border-[var(--line)]">
        <b>Profit share</b> = (Gross − all expenses) ÷ 2. Each person gets reimbursed for expenses they personally paid (reimbursement_to). <b>Salary alloc</b> = <b>approved</b> paid salaries to NK/RR-tagged people in People — auto-deducted from the respective payable. Entries pending approval are held; entries with notes containing "Migrated" are skipped (opening balances). <b>Net Payable</b> = profit share + reimbursement − salary alloc.
      </div>
      <div className="overflow-auto">
        <div className="overflow-x-auto"><table className="table-data w-full">
          <thead><tr>
            <th>Month</th>
            <th className="text-right">Gross Commission</th>
            <th className="text-right">All Expenses</th>
            <th className="text-right">Net Revenue</th>
            <th className="text-right">Profit share (÷2)</th>
            <th className="text-right">NK Reimb.</th>
            <th className="text-right text-[var(--negative)]">NK Salary Alloc</th>
            <th className="text-right bg-[var(--accent)]/5">NK Payable</th>
            <th className="text-center">NK Status</th>
            <th className="text-right">RR Reimb.</th>
            <th className="text-right text-[var(--negative)]">RR Salary Alloc</th>
            <th className="text-right bg-[var(--accent)]/5">RR Payable</th>
            <th className="text-center">RR Status</th>
          </tr></thead>
          <tbody>
            {filtered.map((s) => {
              const nkAlloc = Number(s.nk_salary_alloc || 0);
              const rrAlloc = Number(s.rr_salary_alloc || 0);
              const renderStatus = (person) => {
                const out = Number(person === "NK" ? s.nk_outstanding : s.rr_outstanding);
                const status = person === "NK" ? s.nk_status : s.rr_status;
                if (status === "settled") return <button onClick={() => onOpenSettlement(s, person)} data-testid={`ma-${person.toLowerCase()}-settled-${s.month}`} className="inline-flex items-center gap-1 text-[var(--positive)] text-[11px] font-semibold hover:underline"><CheckCircle2 size={13} /> Settled</button>;
                if (status === "overpaid") return <button onClick={() => onOpenSettlement(s, person)} data-testid={`ma-${person.toLowerCase()}-overpaid-${s.month}`} className="inline-flex items-center gap-1 text-amber-700 text-[11px] font-semibold hover:underline"><CheckCircle2 size={13} /> Overpaid ₹{fmtINR(Math.abs(out), 0)}</button>;
                return <button onClick={() => onOpenSettlement(s, person)} data-testid={`ma-${person.toLowerCase()}-outstanding-${s.month}`} className="inline-flex items-center gap-1 text-[var(--muted)] text-[11px] font-semibold hover:text-[var(--accent)]"><Circle size={13} /> ₹{fmtINR(out, 0)} due</button>;
              };
              return (
                <tr key={s.month} data-testid={`ma-summary-${s.month}`} className={monthFilter === s.month ? "bg-[var(--surface-2)]" : ""}>
                  <td className="num font-medium">{fmtPeriod(s.month)}</td>
                  <td className="text-right num">{fmtINR(s.commission_received, 0)}</td>
                  <td className="text-right num text-[var(--negative)]">{fmtINR(s.total_expense, 0)}</td>
                  <td className="text-right num">{fmtINR(s.after_expense, 0)}</td>
                  <td className="text-right num text-[var(--muted)]">{fmtINR(s.profit_share, 0)}</td>
                  <td className="text-right num text-[12px]">{fmtINR(s.nk_reimbursement, 0)}</td>
                  <td className="text-right num text-[12px] text-[var(--negative)]">
                    {nkAlloc ? <button onClick={() => onOpenAlloc(s, "NK")} data-testid={`ma-nk-alloc-${s.month}`} className="hover:underline cursor-pointer">−{fmtINR(nkAlloc, 0)}</button> : <span className="text-[var(--muted)]">—</span>}
                  </td>
                  <td className="text-right num font-semibold text-[var(--accent)] bg-[var(--accent)]/5">{fmtINR(s.nk_payable, 0)}</td>
                  <td className="text-center">{renderStatus("NK")}</td>
                  <td className="text-right num text-[12px]">{fmtINR(s.rr_reimbursement, 0)}</td>
                  <td className="text-right num text-[12px] text-[var(--negative)]">
                    {rrAlloc ? <button onClick={() => onOpenAlloc(s, "RR")} data-testid={`ma-rr-alloc-${s.month}`} className="hover:underline cursor-pointer">−{fmtINR(rrAlloc, 0)}</button> : <span className="text-[var(--muted)]">—</span>}
                  </td>
                  <td className="text-right num font-semibold text-[var(--accent)] bg-[var(--accent)]/5">{fmtINR(s.rr_payable, 0)}</td>
                  <td className="text-center">{renderStatus("RR")}</td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan={13} className="text-center text-[var(--muted)] py-6">No data yet.</td></tr>}
          </tbody>
        </table></div>
      </div>
      <div className="p-2 flex justify-end text-[11px]">
        <button
          onClick={() => setMonthFilter("")}
          className="text-[var(--muted)] hover:text-[var(--ink)] underline-offset-2 hover:underline"
          data-testid="ma-clear-filter"
        >Clear filter</button>
      </div>
    </Section>
  );
}
